import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useApp } from "../context/AppContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authFetch, api } from "../utils/api";
import CitySearchInput from "../components/CitySearchInput";
import PostCard from "../components/PostCard";
import PowerCard from "../components/PowerCard";
import { tTax } from "../utils/taxonomyI18n";
import { t } from "../utils/i18n";
import AvailabilityModal from "../components/AvailabilityModal";
import PhotoLightbox, { GalleryPhoto } from "../components/PhotoLightbox";
import VerifiedBadge from "../components/VerifiedBadge";
import VerificationTracker from "../components/VerificationTracker";
import { compressImage } from "../utils/imageCompress";
import { formatScore } from "../utils/format";
import ScoreBadge from "../components/ScoreBadge";
import CatalogPicker from "../components/CatalogPicker";
import RingsIcon from "../components/date/RingsIcon";
import ImageCropModal from "../components/ImageCropModal";
import { useIsMobile } from "../hooks/useIsMobile";

const MAX_EDIT_FEEDBACK_MEDIA = 6;

const GalleryBigIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8.5v10A2.5 2.5 0 0 0 5.5 21h10"/>
    <rect x="6" y="3" width="15" height="15" rx="2.5"/>
    <circle cx="11" cy="8" r="1.4"/>
    <path d="M6.5 15l3.5-3.8a1.3 1.3 0 0 1 1.9-.05L15 15"/>
  </svg>
);

const LocationPinIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const BriefcaseIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
  </svg>
);
const CakeIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6"/><path d="M4 21h16"/>
    <path d="M12 13V9"/><path d="M12 9c-1 0-1.5-.7-1.5-1.5S12 5 12 3.5c0 1.5 1.5 2.5 1.5 4S13 9 12 9z"/>
    <path d="M8 21v-3"/><path d="M16 21v-3"/>
  </svg>
);
const GenderIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="14" r="5"/><path d="M16.5 3h4.5v4.5"/><path d="M21 3l-6 6"/>
  </svg>
);
const UsersIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const EyeIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);
const GlobeIcon = ({ size = 10 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);
const LockIcon = ({ size = 10 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
// Icône "cycle/répétition" pour la vignette de secours d'un rituel sans photo — même chemin que
// oltodo-web/src/pages/Ritual.tsx (RepeatIcon).
const RepeatIcon = ({ size = 16, color = "white" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 1l4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/>
    <path d="M7 23l-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
  </svg>
);
// Disposition façon "Entre Potes" demandée par le fondateur pour les onglets Communautés/Rituels
// — badge "Public"/"Sur invitation" (icône globe/cadenas) suivi d'un badge compteur, à la place
// du texte gris plat d'avant.
const audienceBadgeRow = (isPublic: boolean, count: number) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
    <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: "#999" }}>
      {isPublic ? <GlobeIcon /> : <LockIcon />} {isPublic ? "Public" : "Sur invitation"}
    </span>
    <span style={{ display: "flex", alignItems: "center", gap: 3, borderRadius: 10, padding: "2px 7px", backgroundColor: "#F0F0F0", fontSize: 11, fontWeight: 700, color: "#0a0a0a" }}>
      <UsersIcon /> {count}
    </span>
  </div>
);
// Étoile fine (contour, pas remplie) — plus épurée que le glyphe ★ utilisé ailleurs dans
// l'app, réservée aux petits badges de compteur sur les miniatures de la galerie.
const ThinStarIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2.5 15.1 9 22 10 17 15.1 18.2 22 12 18.6 5.8 22 7 15.1 2 10 8.9 9" />
  </svg>
);

const VisibilityMini = ({ value, onChange, lang }: { value: string; onChange: (v: string) => void; lang: string }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0, color: "#999" }}>
    <EyeIcon />
    <select
      value={value || "everyone"}
      onChange={e => onChange(e.target.value)}
      style={{ border: "none", background: "transparent", fontSize: 12, fontWeight: 600, color: "#999", cursor: "pointer", outline: "none", appearance: "none" as any, WebkitAppearance: "none" as any, textAlign: "right" as const }}
    >
      {VISIBILITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(lang, o.labelKey)}</option>)}
    </select>
  </div>
);

const GENDER_OPTIONS = [
  { value: "homme", labelKey: "prof_gender_male" },
  { value: "femme", labelKey: "prof_gender_female" },
  { value: "autre", labelKey: "date_activity_other" },
];
const MARITAL_OPTIONS = [
  { value: "celibataire", labelKey: "prof_marital_single" },
  { value: "en_couple", labelKey: "prof_marital_couple" },
  { value: "marie", labelKey: "prof_marital_married" },
  { value: "divorce", labelKey: "prof_marital_divorced" },
  { value: "autre", labelKey: "date_activity_other" },
];
const VISIBILITY_OPTIONS = [
  { value: "everyone", labelKey: "visibility_everyone" },
  { value: "friends", labelKey: "cr_aud_friends" },
  { value: "private", labelKey: "prof_visibility_private" },
];

const TABS = ["Profil", "Infos", "Passions", "Powers", "Rituels", "Amis", "Communautés", "Galerie"];
const TAB_LABEL_KEYS: Record<string, string> = { "Profil": "prof_tab_profile", "Infos": "prof_tab_infos", "Passions": "prof_tab_passions", "Powers": "prof_tab_powers", "Rituels": "prof_tab_rituals", "Amis": "prof_tab_friends", "Communautés": "prof_tab_communities", "Galerie": "prof_tab_gallery" };


export default function Profile() {
  const { token, user: ctxUser, setUser, lang } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") && TABS.includes(searchParams.get("tab")!) ? searchParams.get("tab")! : "Profil");
  const tabsRef = useRef<HTMLDivElement>(null);

  // Changer d'onglet doit aussi amener visuellement le contenu concerné à l'écran — sans ça,
  // cliquer "Amis"/"Publications" en haut semblait figé puisque rien ne bougeait au-delà du
  // changement (invisible) d'onglet actif tout en bas de la page.
  const tabContentRef = useRef<HTMLDivElement>(null);
  const goToTab = (tab: string) => {
    setActiveTab(tab);
    setTimeout(() => tabContentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };
  const [myRituals, setMyRituals] = useState<any[]>([]);
  const [loadingRituals, setLoadingRituals] = useState(false);
  const [realUser, setRealUser] = useState<any>(ctxUser);
  const [trackerOpen, setTrackerOpen] = useState(false);
  const [passions, setPassions] = useState<any[]>([]);
  const [powers, setPowers] = useState<any[]>([]);
  const [availabilityFor, setAvailabilityFor] = useState<{ id: string; label: string } | null>(null);
  const [identityBadges, setIdentityBadges] = useState<{ family: string; label: string; count: number }[]>([]);
  const [photos, setPhotos] = useState<GalleryPhoto[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [bio, setBio] = useState("");
  const [editingBio, setEditingBio] = useState(false);
  const [tempBio, setTempBio] = useState("");
  const [showPassionPicker, setShowPassionPicker] = useState(false);
  const [showPowerPicker, setShowPowerPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [myPublications, setMyPublications] = useState<any[]>([]);
  const [editingPub, setEditingPub] = useState<any>(null);
  const [editPubTitle, setEditPubTitle] = useState("");
  const [editPubDescription, setEditPubDescription] = useState("");
  const [editPubPrice, setEditPubPrice] = useState("");
  const [editPubIsFree, setEditPubIsFree] = useState(false);
  const [editPubMaxPlaces, setEditPubMaxPlaces] = useState("");
  const [editPubCoverUrl, setEditPubCoverUrl] = useState("");
  const [uploadingPubCover, setUploadingPubCover] = useState(false);
  const [editPubMedia, setEditPubMedia] = useState<{ url: string; media_type: "image" | "video" }[]>([]);
  const [uploadingEditMedia, setUploadingEditMedia] = useState(false);
  const [savingPub, setSavingPub] = useState(false);
  const [deletingPub, setDeletingPub] = useState<any>(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deletingSubmitting, setDeletingSubmitting] = useState(false);
  const [expandedRequestsId, setExpandedRequestsId] = useState<string | null>(null);
  const [pubParticipants, setPubParticipants] = useState<Record<string, any[]>>({});
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [expandedWaitlistId, setExpandedWaitlistId] = useState<string | null>(null);
  const [pubWaitlist, setPubWaitlist] = useState<Record<string, any[]>>({});
  const [loadingWaitlist, setLoadingWaitlist] = useState(false);
  const [loadingPubs, setLoadingPubs] = useState(false);
  const [myFriends, setMyFriends] = useState<any[]>([]);
  const [myCommunities, setMyCommunities] = useState<any[]>([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [friendRequestsReceived, setFriendRequestsReceived] = useState<any[]>([]);
  const [friendRequestsSent, setFriendRequestsSent] = useState<any[]>([]);
  const [loadingFriendRequests, setLoadingFriendRequests] = useState(false);
  const [requestsView, setRequestsView] = useState<"received" | "sent">(searchParams.get("requests") === "sent" ? "sent" : "received");
  const [requestActionId, setRequestActionId] = useState<string | null>(null);


  useEffect(() => {
    if (!token) return;
    api.get("/users/me")
      .then(data => {
        if (data.user) {
          setRealUser(data.user);
          setBio(data.user.bio || "");
          setTempBio(data.user.bio || "");
          setUser(data.user);
        }
        if (data.passions) setPassions(data.passions);
        if (data.powers) setPowers(data.powers);
        setIdentityBadges(data.identity_badges || []);
      });
  }, [token]);

  useEffect(() => {
    if (activeTab !== "Profil" || !realUser?.id || myPublications.length > 0) return;
    setLoadingPubs(true);
    api.get(`/users/${realUser.id}/publications`)
      .then(data => setMyPublications((data?.publications || []).filter((p: any) => !["power_request", "community", "ritual", "welcome", "move"].includes(p.type))))
      .catch(() => {})
      .finally(() => setLoadingPubs(false));
  }, [activeTab, realUser?.id]);

  const openEditPub = (pub: any) => {
    setEditingPub(pub);
    setEditPubTitle(pub.title || "");
    setEditPubDescription(pub.description || "");
    setEditPubPrice(pub.price ? String(pub.price) : "");
    setEditPubIsFree(!!pub.is_free);
    setEditPubMaxPlaces(pub.max_places ? String(pub.max_places) : "");
    setEditPubCoverUrl(pub.cover_url || "");
    setEditPubMedia(pub.photos && pub.photos.length > 0 ? pub.photos : (pub.cover_url ? [{ url: pub.cover_url, media_type: "image" }] : []));
  };

  // Galerie d'un feedback — identique à la création (jusqu'à 6 photos/vidéos), pour pouvoir
  // en rajouter après coup et profiter du même carrousel que sur le fil.
  const handleEditFeedbackMediaSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    const room = MAX_EDIT_FEEDBACK_MEDIA - editPubMedia.length;
    if (room <= 0) return;
    setUploadingEditMedia(true);
    try {
      for (const file of files.slice(0, room)) {
        const isVideo = file.type.startsWith("video/");
        const toSend = isVideo ? file : await compressImage(file);
        const fd = new FormData();
        fd.append("file", toSend);
        const res = await authFetch("/uploads/publication", { method: "POST", body: fd });
        const data = await res.json();
        if (data?.url) {
          setEditPubMedia(prev => [...prev, { url: data.url, media_type: isVideo ? "video" : "image" }]);
        }
      }
    } catch {
      alert(t(lang, "cr_err_upload_media"));
    } finally {
      setUploadingEditMedia(false);
    }
  };

  const removeEditFeedbackMedia = (url: string) => setEditPubMedia(prev => prev.filter(m => m.url !== url));

  const uploadPubCover = async (file: File) => {
    setUploadingPubCover(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await authFetch("/publications/upload", { method: "POST", body: fd });
      const data = await res.json();
      if (data.url) setEditPubCoverUrl(data.url);
      else alert(t(lang, "cr_err_upload_photo"));
    } catch {
      alert(t(lang, "cr_err_upload_photo"));
    } finally {
      setUploadingPubCover(false);
    }
  };

  const saveEditPub = async () => {
    if (!editingPub) return;
    setSavingPub(true);
    try {
      // Un feedback n'a ni prix, ni places, ni titre modifiable (dérivé de l'activité liée à la
      // création) — seules sa description et sa galerie de souvenirs peuvent évoluer.
      const body = editingPub.type === "feedback"
        ? { description: editPubDescription, media: editPubMedia }
        : {
            title: editPubTitle,
            description: editPubDescription,
            price: editPubIsFree ? 0 : Number(editPubPrice || 0),
            is_free: editPubIsFree,
            max_places: editPubMaxPlaces ? Number(editPubMaxPlaces) : null,
            cover_url: editPubCoverUrl || null,
          };
      const data = await api.put(`/publications/${editingPub.id}`, body);
      if (data?.publication) {
        setMyPublications(prev => prev.map(p => p.id === editingPub.id ? { ...p, ...data.publication } : p));
        setEditingPub(null);
      } else {
        alert(data?.error || t(lang, "prof_err_update"));
      }
    } catch {
      alert(t(lang, "err_connection_short"));
    } finally {
      setSavingPub(false);
    }
  };

  const openDeletePub = (pub: any) => {
    setDeletingPub(pub);
    setDeleteReason("");
    setDeleteError("");
  };

  // Le serveur est seul juge des restrictions (aucun participant, et/ou plus de 72h avant
  // l'événement) — le formulaire se contente d'afficher l'erreur qu'il renvoie le cas échéant.
  const confirmDeletePub = () => {
    if (!deletingPub) return;
    setDeletingSubmitting(true);
    setDeleteError("");
    api.delete(`/publications/${deletingPub.id}`, { reason: deleteReason.trim() || undefined }).then(data => {
      if (data?.error) {
        setDeleteError(data.error);
      } else {
        setMyPublications(prev => prev.filter(p => p.id !== deletingPub.id));
        setDeletingPub(null);
      }
    }).catch(() => setDeleteError("Erreur de connexion")).finally(() => setDeletingSubmitting(false));
  };

  // Même logique de réaction que le fil d'accueil, appliquée à ses propres publications.
  const toggleReaction = (post: any, type: "interested" | "top" | "envieux") => {
    api.post(`/publications/${post.id}/react`, { type }).then(data => {
      if (data?.action) {
        setMyPublications(prev => prev.map(p => p.id === post.id ? {
          ...p,
          tops_count: data.tops_count ?? p.tops_count,
          envieux_count: data.envieux_count ?? p.envieux_count,
          interested_count: data.interested_count ?? p.interested_count,
          my_reactions: data.action === "added"
            ? [...(p.my_reactions || []), type]
            : (p.my_reactions || []).filter((r: string) => r !== type),
        } : p));
      }
    }).catch(() => {});
  };

  // Les demandes de participation ne sont chargées qu'à l'ouverture du volet, pour ne pas
  // multiplier les appels réseau sur une liste de publications qui peut être longue.
  const toggleRequests = (pubId: string) => {
    if (expandedRequestsId === pubId) { setExpandedRequestsId(null); return; }
    setExpandedRequestsId(pubId);
    if (!pubParticipants[pubId]) {
      setLoadingParticipants(true);
      api.get(`/publications/${pubId}/participants`)
        .then(data => setPubParticipants(prev => ({ ...prev, [pubId]: data?.participants || [] })))
        .catch(() => {})
        .finally(() => setLoadingParticipants(false));
    }
  };

  const acceptRequest = (pubId: string, userId: string) => {
    api.put(`/publications/${pubId}/participants/${userId}/accept`, {}).then(data => {
      if (!data?.error) {
        setPubParticipants(prev => ({ ...prev, [pubId]: (prev[pubId] || []).map(p => p.id === userId ? { ...p, status: "confirmed" } : p) }));
        setMyPublications(prev => prev.map(p => p.id === pubId ? { ...p, current_places: (p.current_places || 0) + 1 } : p));
      } else {
        alert(data.error);
      }
    }).catch(() => {});
  };

  const rejectRequest = (pubId: string, userId: string) => {
    api.put(`/publications/${pubId}/participants/${userId}/reject`, {}).then(data => {
      if (!data?.error) {
        setPubParticipants(prev => ({ ...prev, [pubId]: (prev[pubId] || []).filter(p => p.id !== userId) }));
      } else {
        alert(data.error);
      }
    }).catch(() => {});
  };

  // Pointage manuel de présence — secours du check-in QR pour les organisateurs qui n'utilisent
  // pas le QR (voir routes/attendance.ts côté backend).
  const markAttended = (pubId: string, userId: string) => {
    api.post(`/publications/${pubId}/participants/${userId}/mark-attended`, {}).then(data => {
      if (data?.error) { alert(data.error); return; }
      setPubParticipants(prev => ({ ...prev, [pubId]: (prev[pubId] || []).map(p => p.id === userId ? { ...p, attended: true } : p) }));
    }).catch(() => {});
  };

  // Liste d'attente de l'organisateur — même logique de chargement paresseux que les demandes
  // de participation, pour ne pas multiplier les appels réseau.
  const toggleWaitlist = (pubId: string) => {
    if (expandedWaitlistId === pubId) { setExpandedWaitlistId(null); return; }
    setExpandedWaitlistId(pubId);
    if (!pubWaitlist[pubId]) {
      setLoadingWaitlist(true);
      api.get(`/publications/${pubId}/waitlist`)
        .then(data => setPubWaitlist(prev => ({ ...prev, [pubId]: data?.waitlist || [] })))
        .catch(() => {})
        .finally(() => setLoadingWaitlist(false));
    }
  };

  const offerWaitlistSpot = (pubId: string, userId: string) => {
    api.post(`/publications/${pubId}/waitlist/${userId}/offer`, {}).then(data => {
      if (!data?.error) {
        setPubWaitlist(prev => ({ ...prev, [pubId]: (prev[pubId] || []).filter(w => w.user_id !== userId) }));
      } else {
        alert(data.error);
      }
    }).catch(() => {});
  };

  useEffect(() => {
    if (activeTab !== "Amis" || !realUser?.id || myFriends.length > 0) return;
    setLoadingFriends(true);
    api.get(`/users/${realUser.id}/friends`)
      .then(data => setMyFriends(data?.friends || []))
      .catch(() => {})
      .finally(() => setLoadingFriends(false));
  }, [activeTab, realUser?.id]);

  useEffect(() => {
    if (activeTab !== "Amis" || !realUser?.id) return;
    setLoadingFriendRequests(true);
    Promise.all([
      api.get(`/users/me/friend-requests/received`).catch(() => ({ requests: [] })),
      api.get(`/users/me/friend-requests/sent`).catch(() => ({ requests: [] })),
    ])
      .then(([received, sent]) => {
        setFriendRequestsReceived(received?.requests || []);
        setFriendRequestsSent(sent?.requests || []);
      })
      .finally(() => setLoadingFriendRequests(false));
  }, [activeTab, realUser?.id]);

  const acceptFriendRequest = (reqId: string, fromUser: any) => {
    setRequestActionId(reqId);
    api.put(`/users/friend-request/${reqId}/accept`, {})
      .then(() => {
        setFriendRequestsReceived(prev => prev.filter(r => r.id !== reqId));
        setMyFriends(prev => [fromUser, ...prev]);
        setRealUser((prev: any) => prev ? { ...prev, friends_count: (prev.friends_count || 0) + 1 } : prev);
      })
      .catch(() => {})
      .finally(() => setRequestActionId(null));
  };

  const rejectFriendRequest = (reqId: string) => {
    setRequestActionId(reqId);
    api.delete(`/users/friend-request/${reqId}/reject`)
      .then(() => setFriendRequestsReceived(prev => prev.filter(r => r.id !== reqId)))
      .catch(() => {})
      .finally(() => setRequestActionId(null));
  };

  const cancelFriendRequest = (reqId: string) => {
    setRequestActionId(reqId);
    api.delete(`/users/friend-request/${reqId}/cancel`)
      .then(() => setFriendRequestsSent(prev => prev.filter(r => r.id !== reqId)))
      .catch(() => {})
      .finally(() => setRequestActionId(null));
  };

  useEffect(() => {
    if (activeTab !== "Communautés" || !realUser?.id || myCommunities.length > 0) return;
    setLoadingCommunities(true);
    api.get(`/users/${realUser.id}/communities`)
      .then(data => setMyCommunities(data?.communities || []))
      .catch(() => {})
      .finally(() => setLoadingCommunities(false));
  }, [activeTab, realUser?.id]);

  useEffect(() => {
    if (activeTab !== "Galerie" || !realUser?.id || photos.length > 0) return;
    setLoadingPhotos(true);
    api.get(`/users/${realUser.id}/photos`)
      .then(data => setPhotos(data?.photos || []))
      .catch(() => {})
      .finally(() => setLoadingPhotos(false));
  }, [activeTab, realUser?.id]);

  // Arrivée depuis une notification "photo aimée" (?photo=<id>) — ouvre directement la
  // visionneuse sur la photo concernée une fois la galerie chargée.
  useEffect(() => {
    const photoId = searchParams.get("photo");
    if (!photoId || activeTab !== "Galerie" || photos.length === 0) return;
    const idx = photos.findIndex((p: any) => p.id === photoId);
    if (idx >= 0) setLightboxIndex(idx);
  }, [activeTab, photos, searchParams]);

  useEffect(() => {
    if (activeTab !== "Rituels" || myRituals.length > 0) return;
    setLoadingRituals(true);
    api.get("/rituals/me")
      .then(data => setMyRituals(data?.rituals || []))
      .catch(() => {})
      .finally(() => setLoadingRituals(false));
  }, [activeTab]);

  const uploadFile = async (file: File | Blob, type: "avatar" | "cover") => {
    const formData = new FormData();
    formData.append("file", file, `${type}.webp`);
    const res = await authFetch(`/uploads/${type}`, { method: "POST", body: formData });
    return res.json();
  };

  // Recadrage avant envoi — jamais d'upload direct du fichier choisi (voir ImageCropModal.tsx :
  // le mobile a déjà ce recentrage via le crop natif d'expo-image-picker, la version web n'avait
  // aucune étape équivalente jusqu'ici).
  const [cropTarget, setCropTarget] = useState<{ type: "avatar" | "cover"; file: File } | null>(null);
  const confirmCrop = async (blob: Blob) => {
    if (!cropTarget) return;
    const { type } = cropTarget;
    setCropTarget(null);
    const data = await uploadFile(blob, type);
    if (data.url) {
      const field = type === "avatar" ? "avatar_url" : "cover_url";
      await api.put("/users/me", { [field]: data.url });
      updateUser({ [field]: data.url });
    }
  };

  const uploadGalleryPhoto = async (file: File) => {
    if (!realUser?.id) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", await compressImage(file));
      const uploadRes = await authFetch("/uploads/profile-gallery", { method: "POST", body: formData }).then(r => r.json());
      if (uploadRes?.url) {
        const data = await api.post(`/users/${realUser.id}/photos`, { url: uploadRes.url });
        if (data?.photo) setPhotos(prev => [data.photo, ...prev]);
      }
    } catch {
      alert(t(lang, "cr_err_upload_photo"));
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Met à jour à la fois l'état local de la page ET le contexte global (utilisé par
  // Home, Explorer, Layout...), pour que les modifications de profil soient visibles
  // partout immédiatement sans nécessiter une reconnexion.
  const updateUser = (patch: Record<string, any>) => {
    const next = { ...realUser, ...patch };
    setRealUser(next);
    setUser(next);
  };

  const saveField = async (field: string, value: string) => {
    setSaving(true);
    try {
      const data = await api.put("/users/me", { [field]: value });
      if (data?.error) {
        alert(t(lang, "prof_err_save"));
      } else {
        updateUser({ [field]: value });
      }
    } catch {
      alert(t(lang, "prof_err_save_conn"));
    } finally {
      setSaving(false);
    }
  };

  const saveCity = async (city: string, lat: number, lon: number) => {
    setSaving(true);
    try {
      const data = await api.put("/users/me", { city, latitude: lat, longitude: lon });
      if (data?.error) {
        alert(t(lang, "prof_err_save"));
      } else {
        updateUser({ city, latitude: lat, longitude: lon });
      }
    } catch {
      alert(t(lang, "prof_err_save_conn"));
    } finally {
      setSaving(false);
    }
  };

  const addPassion = async (label: string) => {
    const data = await api.post("/users/me/passions", { label, icon: "⭐" });
    if (data.passion) setPassions(prev => [...prev, data.passion]);
  };

  const removePassion = async (id: string) => {
    await api.delete(`/users/me/passions/${id}`);
    setPassions(prev => prev.filter(p => p.id !== id));
  };

  const initials = realUser?.name?.split(" ").map((n: string) => n[0]).join("").slice(0,2).toUpperCase() || "?";
  const card: React.CSSProperties = { backgroundColor: "white", borderRadius: 16, padding: 20, marginBottom: 12, border: "1px solid #F0F0F0" };

  // Lignes compactes de l'onglet Infos — une icône par donnée, la valeur et sa préférence de
  // visibilité sur la même ligne plutôt qu'empilées dans des blocs séparés (bien plus léger
  // visuellement qu'une grande case bordée par champ).
  const infoRowStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 14, padding: "14px 4px", borderBottom: "1px solid #F5F5F5" };
  const infoIconWrap: React.CSSProperties = { width: 38, height: 38, borderRadius: 11, backgroundColor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#666" };
  const infoRowLabel: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, color: "#999", textTransform: "uppercase" as const, letterSpacing: 0.4, marginBottom: 3 };
  const bareInput: React.CSSProperties = { width: "100%", border: "none", outline: "none", background: "transparent", fontSize: 14, color: "#0a0a0a", padding: 0, fontFamily: "inherit" };
  const bareSelect: React.CSSProperties = { ...bareInput, cursor: "pointer", appearance: "none" as any, WebkitAppearance: "none" as any };

  return (
    <div style={{ display: "flex", height: isMobile ? "auto" : "calc(100vh - 60px)", overflow: isMobile ? "visible" : "hidden", flexDirection: isMobile ? "column" : "row" }}>

      {/* CONTENU PRINCIPAL */}
      <div style={{ flex: 1, overflowY: isMobile ? "visible" : "auto" }}>

        {/* COVER — cliquable */}
        <label style={{ display: "block", cursor: "pointer", position: "relative" }}>
          <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) setCropTarget({ type: "cover", file });
          }} />
          <div style={{ height: 200, backgroundColor: "#1a1a1a", backgroundImage: realUser?.cover_url ? `url(${realUser.cover_url})` : "none", backgroundSize: "cover", backgroundPosition: "center", position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s" }}
              onMouseOver={e => (e.currentTarget.style.opacity = "1")}
              onMouseOut={e => (e.currentTarget.style.opacity = "0")}>
              <div style={{ backgroundColor: "rgba(0,0,0,0.6)", borderRadius: 20, padding: "8px 16px" }}>
                <span style={{ color: "white", fontSize: 13, fontWeight: 600 }}>{t(lang, "venue_change_cover")}</span>
              </div>
            </div>
          </div>
        </label>

        {/* AVATAR — cliquable */}
        <div style={{ padding: "0 24px", position: "relative" }}>
          <label style={{ cursor: "pointer", display: "inline-block", marginTop: -40, position: "relative" }}>
            <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) setCropTarget({ type: "avatar", file });
            }} />
            <div style={{ width: 80, height: 80, borderRadius: "50%", backgroundColor: "#0a0a0a", border: "4px solid white", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
              {realUser?.avatar_url
                ? <img src={realUser.avatar_url} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <span style={{ color: "white", fontWeight: 800, fontSize: 24 }}>{initials}</span>
              }
              <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s", borderRadius: "50%" }}
                onMouseOver={e => (e.currentTarget.style.opacity = "1")}
                onMouseOut={e => (e.currentTarget.style.opacity = "0")}>
                <span style={{ color: "white", fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>{t(lang, "edit")}</span>
              </div>
            </div>
          </label>

          {/* Nom + infos */}
          <div style={{ marginTop: 12, marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{realUser?.name || t(lang, "prof_my_profile")}</h1>
              {realUser?.is_verified && (
                <button onClick={() => setTrackerOpen(true)} aria-label={t(lang, "vfy_track_title")} style={{ padding: 0, border: "none", background: "none", cursor: "pointer", display: "flex" }}><VerifiedBadge size={18} /></button>
              )}
              {trackerOpen && realUser?.id && <VerificationTracker userId={realUser.id} onClose={() => setTrackerOpen(false)} />}
            </div>
            <div style={{ fontSize: 14, color: "#666" }}>
              {[realUser?.job_title, realUser?.city].filter(Boolean).join(" · ") || t(lang, "prof_complete_profile")}
            </div>
            {identityBadges.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6, marginTop: 8 }}>
                {identityBadges.map(b => (
                  <span key={b.family} title={`${b.count} expériences vécues en ${b.family}`} style={{ fontSize: 11, fontWeight: 700, color: "#0a0a0a", backgroundColor: "#F0F0F0", border: "1px solid #E5E5E5", borderRadius: 12, padding: "3px 10px" }}>
                    {b.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Compte non vérifié : même bandeau permanent que côté mobile (app/(tabs)/profile.tsx)
              — is_verified est aussi le flag qui bloque toute action d'écriture côté serveur. */}
          {!realUser?.is_verified && (
            <div onClick={() => navigate("/verify-identity")} style={{ display: "flex", alignItems: "center", gap: 10, backgroundColor: "#F8F8F8", border: "1px solid #E5E5E5", borderRadius: 14, padding: 14, marginBottom: 16, cursor: "pointer" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{t(lang, "verify_identity_banner_title")}</div>
                <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>{t(lang, "vfy_banner_body")}</div>
              </div>
              <span style={{ color: "#999" }}>›</span>
            </div>
          )}

          {/* Stats — "Score" est cliquable : c'est l'accès au détail du Score Social
              (notes reçues, anonymes) et aux personnes à noter, notamment sur mobile où
              il n'y a pas d'onglet dédié dans la barre du bas. */}
          <div style={{ display: "flex", backgroundColor: "#F8F8F8", borderRadius: 14, overflow: "hidden", marginBottom: 16 }}>
            {[
              { num: formatScore(realUser?.score), label: "Score", onClick: () => navigate("/rating") },
              { num: realUser?.friends_count || 0, label: "Amis", onClick: () => goToTab("Amis") },
              { num: realUser?.publications_count || 0, label: "Publications", onClick: () => goToTab("Profil") },
            ].map((s, i) => (
              <div key={i} onClick={s.onClick} style={{ flex: 1, textAlign: "center", padding: "14px 8px", borderRight: i < 2 ? "1px solid #F0F0F0" : "none", cursor: "pointer" }}>
                <div style={{ fontWeight: 800, fontSize: 20, color: "#0a0a0a" }}>{s.num}</div>
                <div style={{ fontSize: 11, color: "#999", marginTop: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Bio */}
          {editingBio ? (
            <div style={{ marginBottom: 16 }}>
              <textarea value={tempBio} onChange={e => setTempBio(e.target.value)} maxLength={300}
                style={{ width: "100%", padding: "12px", borderRadius: 12, border: "1.5px solid #0a0a0a", fontSize: 14, resize: "none", height: 100, outline: "none" }} />
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={() => setEditingBio(false)} style={{ flex: 1, padding: "8px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "white", cursor: "pointer" }}>{t(lang, "cancel")}</button>
                <button onClick={async () => { await saveField("bio", tempBio); setBio(tempBio); setEditingBio(false); }} style={{ flex: 1, padding: "8px", borderRadius: 10, border: "none", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer", fontWeight: 600 }}>
                  {saving ? "..." : "Sauvegarder"}
                </button>
              </div>
            </div>
          ) : (
            <div onClick={() => setEditingBio(true)} style={{ marginBottom: 16, cursor: "pointer", padding: "12px", backgroundColor: "#F8F8F8", borderRadius: 12, minHeight: 50 }}>
              <p style={{ fontSize: 14, color: bio ? "#555" : "#999", margin: 0, lineHeight: 1.6 }}>
                {bio || "Cliquez pour ajouter une bio..."}
              </p>
              <div style={{ fontSize: 11, color: "#0a0a0a", marginTop: 6, fontWeight: 700 }}>
                Modifier
              </div>
            </div>
          )}
        </div>

        {/* TABS */}
        <div ref={tabsRef} style={{ display: "flex", borderBottom: "1px solid #F0F0F0", overflowX: "auto", backgroundColor: "white", position: "sticky", top: 0, zIndex: 10 }}>
          {TABS.map(tab => (
            <button key={tab} onClick={() => goToTab(tab)} style={{
              padding: "12px 16px", border: "none", cursor: "pointer", whiteSpace: "nowrap",
              backgroundColor: "transparent",
              borderBottom: activeTab === tab ? "2px solid #0a0a0a" : "2px solid transparent",
              color: activeTab === tab ? "#0a0a0a" : "#999",
              fontWeight: activeTab === tab ? 700 : 400, fontSize: 13,
            }}>{t(lang, TAB_LABEL_KEYS[tab] || tab)}</button>
          ))}
        </div>

        <div ref={tabContentRef} style={{ padding: "20px 24px", scrollMarginTop: 52 }}>

          {/* ONGLET PROFIL */}
          {activeTab === "Profil" && (
            <div>
              {loadingPubs && (
                <div style={{ fontSize: 14, color: "#999", textAlign: "center", padding: "40px 0" }}>Chargement…</div>
              )}
              {!loadingPubs && myPublications.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
                  <div style={{ fontSize: 14, marginBottom: 16 }}>{t(lang, "prof_no_posts")}</div>
                  <button onClick={() => navigate("/create")} style={{ backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "10px 24px", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>+ Créer une publication</button>
                </div>
              )}
              {myPublications.map((pub: any) => (
                <PostCard
                  key={pub.id}
                  post={pub}
                  lang={lang}
                  user={realUser}
                  navigate={navigate}
                  toggleReaction={toggleReaction}
                  onEdit={openEditPub}
                  onDelete={openDeletePub}
                  extraFooter={pub.type !== "feedback" ? (
                    <div style={{ borderTop: "1px solid #F8F8F8", padding: "8px 16px 14px" }}>
                      <button onClick={() => toggleRequests(pub.id)} style={{ background: "none", border: "none", color: "#0a0a0a", fontWeight: 600, cursor: "pointer", fontSize: 13, padding: "4px 0" }}>
                        Demandes de participation {expandedRequestsId === pub.id ? "▲" : "▼"}
                      </button>
                      {expandedRequestsId === pub.id && (
                        <div style={{ marginTop: 8 }}>
                          {loadingParticipants && !pubParticipants[pub.id] && <div style={{ fontSize: 13, color: "#999", textAlign: "center", padding: "10px 0" }}>Chargement…</div>}
                          {pubParticipants[pub.id] && pubParticipants[pub.id].length === 0 && (
                            <div style={{ fontSize: 13, color: "#999", textAlign: "center", padding: "10px 0" }}>{t(lang, "notif_requests_empty_short")}</div>
                          )}
                          {(pubParticipants[pub.id] || []).map((participant: any) => (
                            <div key={participant.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #F8F8F8" }}>
                              <div style={{ width: 34, height: 34, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 12, overflow: "hidden", flexShrink: 0 }}>
                                {participant.avatar_url ? <img src={participant.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (participant.name || "?").split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{participant.name}</div>
                                <div style={{ fontSize: 11, color: "#999" }}>★ {formatScore(participant.score)}</div>
                              </div>
                              {participant.status === "confirmed" ? (
                                participant.attended ? (
                                  <span style={{ fontSize: 12, color: "#999", flexShrink: 0 }}>✓ Présent</span>
                                ) : (
                                  <button onClick={() => markAttended(pub.id, participant.id)} style={{ backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 16, padding: "5px 12px", fontSize: 12, fontWeight: 600, color: "#0a0a0a", cursor: "pointer", flexShrink: 0 }}>{t(lang, "prof_mark_attended")}</button>
                                )
                              ) : (
                                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                  <button onClick={() => rejectRequest(pub.id, participant.id)} style={{ backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 16, padding: "5px 12px", fontSize: 12, fontWeight: 600, color: "#666", cursor: "pointer" }}>{t(lang, "date_action_decline")}</button>
                                  <button onClick={() => acceptRequest(pub.id, participant.id)} style={{ backgroundColor: "#0a0a0a", border: "none", borderRadius: 16, padding: "5px 12px", fontSize: 12, fontWeight: 600, color: "white", cursor: "pointer" }}>{t(lang, "date_action_accept")}</button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {pub.max_places > 0 && (
                        <>
                          <button onClick={() => toggleWaitlist(pub.id)} style={{ background: "none", border: "none", color: "#0a0a0a", fontWeight: 600, cursor: "pointer", fontSize: 13, padding: "8px 0 4px", marginTop: 4 }}>
                            Liste d'attente {expandedWaitlistId === pub.id ? "▲" : "▼"}
                          </button>
                          {expandedWaitlistId === pub.id && (
                            <div style={{ marginTop: 8 }}>
                              {loadingWaitlist && !pubWaitlist[pub.id] && <div style={{ fontSize: 13, color: "#999", textAlign: "center", padding: "10px 0" }}>Chargement…</div>}
                              {pubWaitlist[pub.id] && pubWaitlist[pub.id].length === 0 && (
                                <div style={{ fontSize: 13, color: "#999", textAlign: "center", padding: "10px 0" }}>{t(lang, "prof_waitlist_empty")}</div>
                              )}
                              {(pubWaitlist[pub.id] || []).map((w: any, i: number) => (
                                <div key={w.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #F8F8F8" }}>
                                  <div style={{ fontSize: 11, color: "#999", fontWeight: 700, width: 16, flexShrink: 0 }}>#{i + 1}</div>
                                  <div style={{ width: 34, height: 34, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 12, overflow: "hidden", flexShrink: 0 }}>
                                    {w.avatar_url ? <img src={w.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (w.name || "?").split(" ").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase()}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{w.name}</div>
                                    <div style={{ fontSize: 11, color: "#999" }}>★ {formatScore(w.score)}</div>
                                  </div>
                                  <button onClick={() => offerWaitlistSpot(pub.id, w.user_id)} style={{ backgroundColor: "#0a0a0a", border: "none", borderRadius: 16, padding: "5px 12px", fontSize: 12, fontWeight: 600, color: "white", cursor: "pointer", flexShrink: 0 }}>{t(lang, "prof_offer")}</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ) : undefined}
                />
              ))}
            </div>
          )}

          {/* ONGLET INFOS */}
          {activeTab === "Infos" && (
            <div style={{ maxWidth: 600 }}>
              {/* Ville — obligatoire, contrôlée par géolocalisation comme à l'inscription */}
              <div style={{ ...card, marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div style={infoIconWrap}><LocationPinIcon /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={infoRowLabel}>{t(lang, "city")} <span style={{ color: "#c00" }}>*</span></div>
                    <CitySearchInput
                      city={realUser?.city || ""}
                      initiallySelected={!!(realUser?.city && realUser?.latitude && realUser?.longitude)}
                      onSelect={(label, lat, lon) => saveCity(label, lat, lon)}
                      placeholder={t(lang, "prof_ph_city")}
                    />
                  </div>
                </div>
              </div>

              <div style={card}>
                {/* Métier */}
                <div style={infoRowStyle}>
                  <div style={infoIconWrap}><BriefcaseIcon /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={infoRowLabel}>{t(lang, "prof_job")}</div>
                    <input
                      key={realUser?.job_title || ""}
                      defaultValue={realUser?.job_title || ""}
                      placeholder={t(lang, "prof_ph_add")}
                      onBlur={async e => { if (e.target.value !== (realUser?.job_title || "")) await saveField("job_title", e.target.value); }}
                      style={bareInput}
                    />
                  </div>
                </div>

                {/* Date de naissance */}
                <div style={infoRowStyle}>
                  <div style={infoIconWrap}><CakeIcon /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={infoRowLabel}>{t(lang, "prof_birthdate")}</div>
                    <input
                      key={realUser?.birthday || ""}
                      type="date"
                      defaultValue={realUser?.birthday ? realUser.birthday.slice(0, 10) : ""}
                      onBlur={async e => { if (e.target.value && e.target.value !== (realUser?.birthday || "").slice(0, 10)) await saveField("birthday", e.target.value); }}
                      style={{ ...bareInput, colorScheme: "light" as any, height: 20, lineHeight: "20px" }}
                    />
                  </div>
                  <VisibilityMini value={realUser?.birthday_visibility} onChange={v => saveField("birthday_visibility", v)} lang={lang} />
                </div>

                {/* Sexe */}
                <div style={infoRowStyle}>
                  <div style={infoIconWrap}><GenderIcon /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={infoRowLabel}>{t(lang, "prof_gender")}</div>
                    <select
                      value={realUser?.gender || ""}
                      onChange={e => saveField("gender", e.target.value)}
                      style={bareSelect}
                    >
                      <option value="" disabled>{t(lang, "prof_choose")}</option>
                      {GENDER_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(lang, o.labelKey)}</option>)}
                    </select>
                  </div>
                  <VisibilityMini value={realUser?.gender_visibility} onChange={v => saveField("gender_visibility", v)} lang={lang} />
                </div>

                {/* Statut marital */}
                <div style={infoRowStyle}>
                  <div style={infoIconWrap}><RingsIcon /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={infoRowLabel}>{t(lang, "prof_marital")}</div>
                    <select
                      value={realUser?.marital_status || ""}
                      onChange={e => saveField("marital_status", e.target.value)}
                      style={bareSelect}
                    >
                      <option value="" disabled>{t(lang, "prof_choose")}</option>
                      {MARITAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{t(lang, o.labelKey)}</option>)}
                    </select>
                  </div>
                  <VisibilityMini value={realUser?.marital_status_visibility} onChange={v => saveField("marital_status_visibility", v)} lang={lang} />
                </div>

                {/* Confidentialité de la liste d'amis — le nombre d'amis, lui, reste toujours visible */}
                <div style={{ ...infoRowStyle, borderBottom: "none" }}>
                  <div style={infoIconWrap}><UsersIcon /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={infoRowLabel}>{t(lang, "prof_friends_list")}</div>
                    <div style={{ fontSize: 12.5, color: "#999" }}>Le nombre reste toujours visible</div>
                  </div>
                  <VisibilityMini value={realUser?.friends_list_visibility} onChange={v => saveField("friends_list_visibility", v)} lang={lang} />
                </div>
              </div>
            </div>
          )}

          {/* ONGLET PASSIONS */}
          {activeTab === "Passions" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{passions.length} passion{passions.length > 1 ? "s" : ""}</span>
                <button onClick={() => setShowPassionPicker(true)} style={{ display: "flex", alignItems: "center", gap: 6, backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  + Ajouter
                </button>
              </div>

              {passions.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#999" }}>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{t(lang, "prof_no_passions")}</div>
                  <div style={{ fontSize: 13 }}>{t(lang, "prof_no_passions_sub")}</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {passions.map((p: any) => (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, backgroundColor: "#F0F0F0", borderRadius: 20, padding: "8px 14px" }}>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>{tTax(lang, p.label)}</span>
                      <button onClick={() => removePassion(p.id)} style={{ border: "none", background: "none", cursor: "pointer", padding: 0, display: "flex", fontSize: 16, fontWeight: 700, color: "#999", lineHeight: 1 }}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Picker passions */}
              {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
                  position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
                  "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
                  vrai viewport. */}
              {showPassionPicker && ReactDOM.createPortal(
                <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                  <div style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", width: "100%", maxWidth: 600, maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px 12px" }}>
                      <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "onboarding_welcome_pick_passion_title")}</span>
                      <button onClick={() => setShowPassionPicker(false)} style={{ border: "none", background: "none", fontSize: 24, cursor: "pointer", color: "#999" }}>✕</button>
                    </div>
                    <CatalogPicker type="passion" mode="multiple" selected={passions.map((p: any) => p.label)} onSelect={addPassion} />
                  </div>
                </div>,
                document.body
              )}
            </div>
          )}

          {/* ONGLET POWERS */}
          {activeTab === "Powers" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{powers.length} power{powers.length > 1 ? "s" : ""}</span>
                <button onClick={() => setShowPowerPicker(true)} style={{ display: "flex", alignItems: "center", gap: 6, backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  + Ajouter
                </button>
              </div>

              {powers.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#999" }}>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{t(lang, "prof_no_powers")}</div>
                  <div style={{ fontSize: 13 }}>{t(lang, "prof_no_powers_sub")}</div>
                </div>
              ) : (
                <div>
                  {powers.map((p: any) => (
                    <PowerCard
                      key={p.id}
                      power={{ id: p.id, label: p.label, years_experience: p.years_experience, description: p.description, photos: p.photos || [], missions_count: p.missions_count || 0 }}
                      isOwner
                      onDeleted={async () => {
                        await api.delete(`/users/me/powers/${p.id}`);
                        setPowers(prev => prev.filter((pp: any) => pp.id !== p.id));
                      }}
                      onPhotosChange={(photos) => setPowers(prev => prev.map((pp: any) => pp.id === p.id ? { ...pp, photos } : pp))}
                      onManageAvailability={() => setAvailabilityFor({ id: p.id, label: p.label })}
                    />
                  ))}
                </div>
              )}

              {/* Picker powers */}
              {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
                  position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
                  "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
                  vrai viewport. */}
              {showPowerPicker && ReactDOM.createPortal(
                <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                  <div style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", width: "100%", maxWidth: 600, maxHeight: "80vh", display: "flex", flexDirection: "column" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px 12px" }}>
                      <span style={{ fontSize: 18, fontWeight: 700 }}>{t(lang, "onboarding_welcome_pick_power_title")}</span>
                      <button onClick={() => setShowPowerPicker(false)} style={{ border: "none", background: "none", fontSize: 24, cursor: "pointer", color: "#999" }}>✕</button>
                    </div>
                    <CatalogPicker
                      type="power"
                      mode="multiple"
                      selected={powers.map((p: any) => p.label)}
                      onSelect={async (label) => {
                        const data = await api.post("/users/me/powers", { label, icon: "⚡" });
                        if (data.power) setPowers(prev => [...prev, data.power]);
                      }}
                    />
                  </div>
                </div>,
                document.body
              )}

              {availabilityFor && (
                <AvailabilityModal resource={{ type: "power", id: availabilityFor.id, label: availabilityFor.label }} onClose={() => setAvailabilityFor(null)} />
              )}
            </div>
          )}

          {/* ONGLET AMIS */}
          {activeTab === "Amis" && (
            <div>
              {(loadingFriendRequests || friendRequestsReceived.length > 0 || friendRequestsSent.length > 0) && (
                <div style={{ marginBottom: 24, border: "1px solid #F0F0F0", borderRadius: 16, overflow: "hidden" }}>
                  <div style={{ display: "flex", borderBottom: "1px solid #F0F0F0" }}>
                    {(["received", "sent"] as const).map(view => (
                      <button
                        key={view}
                        onClick={() => setRequestsView(view)}
                        style={{
                          flex: 1, padding: "12px 8px", border: "none", cursor: "pointer",
                          backgroundColor: requestsView === view ? "#0a0a0a" : "white",
                          color: requestsView === view ? "white" : "#666",
                          fontSize: 13, fontWeight: 700,
                        }}
                      >
                        {view === "received" ? t(lang, "prof_received") : t(lang, "prof_sent")}
                        {(view === "received" ? friendRequestsReceived.length : friendRequestsSent.length) > 0 &&
                          ` (${view === "received" ? friendRequestsReceived.length : friendRequestsSent.length})`}
                      </button>
                    ))}
                  </div>
                  <div style={{ padding: 12 }}>
                    {loadingFriendRequests && <div style={{ textAlign: "center", padding: "16px 0", color: "#999", fontSize: 13 }}>{t(lang, "loading_ellipsis")}</div>}

                    {!loadingFriendRequests && requestsView === "received" && friendRequestsReceived.length === 0 && (
                      <div style={{ textAlign: "center", padding: "16px 0", color: "#999", fontSize: 13 }}>{t(lang, "prof_no_requests_received")}</div>
                    )}
                    {!loadingFriendRequests && requestsView === "received" && friendRequestsReceived.map((r: any) => (
                      <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 4px" }}>
                        <div onClick={() => navigate(`/profile/${r.user_id}`)} style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 14, overflow: "hidden", cursor: "pointer", flexShrink: 0 }}>
                          {r.avatar_url ? <img src={r.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (r.name || "?").split(" ").map((n: string) => n[0]).join("").slice(0,2).toUpperCase()}
                        </div>
                        <div onClick={() => navigate(`/profile/${r.user_id}`)} style={{ flex: 1, cursor: "pointer", minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</div>
                          <div style={{ fontSize: 12, color: "#999" }}>★ {formatScore(r.score)}</div>
                        </div>
                        <button
                          disabled={requestActionId === r.id}
                          onClick={() => rejectFriendRequest(r.id)}
                          style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid #E0E0E0", backgroundColor: "white", cursor: "pointer", color: "#999", fontSize: 16, flexShrink: 0, opacity: requestActionId === r.id ? 0.5 : 1 }}
                        >✕</button>
                        <button
                          disabled={requestActionId === r.id}
                          onClick={() => acceptFriendRequest(r.id, { id: r.user_id, name: r.name, avatar_url: r.avatar_url, score: r.score })}
                          style={{ width: 32, height: 32, borderRadius: "50%", border: "none", backgroundColor: "#0a0a0a", cursor: "pointer", color: "white", fontSize: 16, flexShrink: 0, opacity: requestActionId === r.id ? 0.5 : 1 }}
                        >✓</button>
                      </div>
                    ))}

                    {!loadingFriendRequests && requestsView === "sent" && friendRequestsSent.length === 0 && (
                      <div style={{ textAlign: "center", padding: "16px 0", color: "#999", fontSize: 13 }}>{t(lang, "prof_no_requests_sent")}</div>
                    )}
                    {!loadingFriendRequests && requestsView === "sent" && friendRequestsSent.map((r: any) => (
                      <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 4px" }}>
                        <div onClick={() => navigate(`/profile/${r.user_id}`)} style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 14, overflow: "hidden", cursor: "pointer", flexShrink: 0 }}>
                          {r.avatar_url ? <img src={r.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (r.name || "?").split(" ").map((n: string) => n[0]).join("").slice(0,2).toUpperCase()}
                        </div>
                        <div onClick={() => navigate(`/profile/${r.user_id}`)} style={{ flex: 1, cursor: "pointer", minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{r.name}</div>
                          <div style={{ fontSize: 12, color: "#999" }}>{t(lang, "tx_status_pending")}</div>
                        </div>
                        <button
                          disabled={requestActionId === r.id}
                          onClick={() => cancelFriendRequest(r.id)}
                          style={{ padding: "6px 12px", borderRadius: 16, border: "1px solid #E0E0E0", backgroundColor: "white", cursor: "pointer", color: "#666", fontSize: 12, fontWeight: 600, flexShrink: 0, opacity: requestActionId === r.id ? 0.5 : 1 }}
                        >{t(lang, "cancel")}</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loadingFriends && <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>Chargement…</div>}
              {!loadingFriends && myFriends.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#999" }}>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{t(lang, "prof_no_friends")}</div>
                  <div style={{ fontSize: 13 }}>{t(lang, "prof_no_friends_sub")}</div>
                  <button onClick={() => navigate("/explore")} style={{ marginTop: 16, backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "10px 24px", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>{t(lang, "explore")}</button>
                </div>
              )}
              {!loadingFriends && myFriends.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {myFriends.map((f: any) => (
                    <div key={f.id} onClick={() => navigate(`/profile/${f.id}`)} style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, borderRadius: 12, border: "1px solid #F0F0F0", cursor: "pointer" }}>
                      <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 14, overflow: "hidden" }}>
                        {f.avatar_url ? <img src={f.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (f.name || "?").split(" ").map((n: string) => n[0]).join("").slice(0,2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{f.name}</div>
                        <div style={{ fontSize: 12, color: "#999" }}>★ {formatScore(f.score)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ONGLET COMMUNAUTÉS */}
          {activeTab === "Communautés" && (
            <div>
              {loadingCommunities && <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>Chargement…</div>}
              {!loadingCommunities && myCommunities.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#999" }}>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{t(lang, "prof_no_communities")}</div>
                  <div style={{ fontSize: 13 }}>{t(lang, "prof_no_communities_sub")}</div>
                  <button onClick={() => navigate("/create")} style={{ marginTop: 16, backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "10px 24px", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>{t(lang, "prof_create_community")}</button>
                </div>
              )}
              {!loadingCommunities && myCommunities.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {myCommunities.map((c: any) => (
                    <div key={c.id} onClick={() => navigate(`/community/${c.id}`)} style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, borderRadius: 12, border: "1px solid #F0F0F0", cursor: "pointer" }}>
                      <div style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: "#0a0a0a", backgroundImage: c.cover_url ? `url(${c.cover_url})` : "none", backgroundSize: "cover", backgroundPosition: "center", flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                        {audienceBadgeRow(c.adhesion === "public", c.members_count || 0)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ONGLET GALERIE */}
          {activeTab === "Galerie" && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{photos.length} photo{photos.length > 1 ? "s" : ""}</span>
                <label style={{ display: "flex", alignItems: "center", gap: 6, backgroundColor: "#0a0a0a", color: "white", borderRadius: 20, padding: "8px 16px", cursor: uploadingPhoto ? "default" : "pointer", fontSize: 13, fontWeight: 600, opacity: uploadingPhoto ? 0.6 : 1 }}>
                  <input type="file" accept="image/*" multiple disabled={uploadingPhoto} style={{ display: "none" }} onChange={async e => {
                    const files = Array.from(e.target.files || []);
                    e.target.value = "";
                    for (const file of files) await uploadGalleryPhoto(file);
                  }} />
                  {uploadingPhoto ? "Envoi..." : "+ Ajouter des photos"}
                </label>
              </div>

              {loadingPhotos && <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>Chargement…</div>}

              {!loadingPhotos && photos.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#999" }}>
                  <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><GalleryBigIcon /></div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{t(lang, "prof_no_photos")}</div>
                  <div style={{ fontSize: 13 }}>{t(lang, "prof_no_photos_sub")}</div>
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
                  {photos.map((p, i) => (
                    <div key={p.id} onClick={() => setLightboxIndex(i)} style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "hidden", cursor: "pointer", backgroundColor: "#F0F0F0", ...(p.media_type !== "video" ? { backgroundImage: `url(${p.url})`, backgroundSize: "cover", backgroundPosition: "center" } : {}) }}>
                      {p.media_type === "video" && (
                        <>
                          <video src={p.url} muted preload="metadata" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                          <span style={{ position: "absolute", top: 6, left: 6, fontSize: 9.5, fontWeight: 800, color: "white", backgroundColor: "#e74c3c", borderRadius: 5, padding: "2px 5px", letterSpacing: 0.5 }}>(live)</span>
                          <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <span style={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><polygon points="6,4 20,12 6,20" /></svg>
                            </span>
                          </span>
                        </>
                      )}
                      {(Number(p.likes_count) > 0 || (p.media_type === "video" && !!p.views_count)) && (
                        <div style={{ position: "absolute", bottom: 6, left: 6, right: 6, display: "flex", justifyContent: "space-between" }}>
                          {Number(p.likes_count) > 0 && (
                            <span style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 10.5, fontWeight: 700, color: "white", textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>
                              <ThinStarIcon /> {p.likes_count}
                            </span>
                          )}
                          {p.media_type === "video" && !!p.views_count && (
                            <span style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 10.5, fontWeight: 700, color: "white", textShadow: "0 1px 3px rgba(0,0,0,0.6)" }}>
                              <EyeIcon /> {p.views_count}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {lightboxIndex !== null && realUser && (
            <PhotoLightbox
              photos={photos}
              index={lightboxIndex}
              onIndexChange={setLightboxIndex}
              onClose={() => setLightboxIndex(null)}
              isOwner
              currentUserId={realUser.id}
              ownerId={realUser.id}
              ownerName={realUser.name}
              ownerAvatar={realUser.avatar_url}
              onLikeToggled={(photoId, liked, likesCount) => setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, liked_by_me: liked, likes_count: likesCount } : p))}
              onLocationUpdated={(photoId, location) => setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, location } : p))}
              onViewed={photoId => setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, views_count: (p.views_count || 0) + 1 } : p))}
              onDeleted={photoId => {
                setPhotos(prev => prev.filter(p => p.id !== photoId));
                setLightboxIndex(null);
              }}
            />
          )}

          {/* ONGLET RITUELS */}
          {activeTab === "Rituels" && (
            <div>
              {loadingRituals && <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>Chargement…</div>}
              {!loadingRituals && myRituals.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#999" }}>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>{t(lang, "prof_no_rituals")}</div>
                  <div style={{ fontSize: 13 }}>{t(lang, "prof_no_rituals_sub")}</div>
                  <button onClick={() => navigate("/create")} style={{ marginTop: 16, backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "10px 24px", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>Créer un rituel</button>
                </div>
              )}
              {!loadingRituals && myRituals.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {/* Même disposition que l'onglet Communautés ci-dessus (photo carrée + badges)
                      — auparavant une carte texte seul, sans la moindre photo alors que
                      cover_url existe et est même obligatoire à la création. */}
                  {myRituals.map((r: any) => (
                    <div key={r.id} onClick={() => navigate(`/ritual/${r.id}`)} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: 12, borderRadius: 12, border: "1px solid #F0F0F0", cursor: "pointer" }}>
                      <div style={{ width: 40, height: 40, borderRadius: 12, backgroundColor: "#0a0a0a", backgroundImage: r.cover_url ? `url(${r.cover_url})` : "none", backgroundSize: "cover", backgroundPosition: "center", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {!r.cover_url && <RepeatIcon size={16} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div style={{ fontWeight: 700, fontSize: 15 }}>{r.title}</div>
                          {r.category && <span style={{ backgroundColor: "#F0F0F0", borderRadius: 20, padding: "3px 10px", fontSize: 11, color: "#666", flexShrink: 0, marginLeft: 8 }}>{r.category}</span>}
                        </div>
                        {audienceBadgeRow(r.audience === "public", r.members_count || 0)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* SIDEBAR DROITE */}
      {!isMobile && (
        <div style={{ width: 280, flexShrink: 0, overflowY: "auto", padding: "20px 20px 20px 0", borderLeft: "1px solid #F0F0F0" }}>
          <div onClick={() => navigate("/rating")} style={{ backgroundColor: "white", borderRadius: 16, padding: 20, border: "1px solid #F0F0F0", marginBottom: 12, cursor: "pointer" }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Score Social</div>
            <div style={{ textAlign: "center" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}><ScoreBadge score={realUser?.score} size={80} /></div>
              <div style={{ fontSize: 12, color: "#999" }}>
                sur 10, {Number(realUser?.score || 0) >= 8 ? "Excellent" : Number(realUser?.score || 0) >= 6 ? "Bien" : "En progression"}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-around", marginTop: 16, paddingTop: 16, borderTop: "1px solid #F0F0F0" }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontWeight: 700, fontSize: 18, color: "#0a0a0a" }}>{realUser?.friends_count || 0}</div>
                <div style={{ fontSize: 11, color: "#999" }}>Amis</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontWeight: 700, fontSize: 18, color: "#0a0a0a" }}>{realUser?.publications_count || 0}</div>
                <div style={{ fontSize: 11, color: "#999" }}>Publications</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontWeight: 700, fontSize: 18, color: "#0a0a0a" }}>{passions.length}</div>
                <div style={{ fontSize: 11, color: "#999" }}>Passions</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {editingPub && ReactDOM.createPortal(
        <div onClick={() => setEditingPub(null)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 17, fontWeight: 700 }}>Modifier la publication</span>
              <button onClick={() => setEditingPub(null)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>

            {editingPub.type === "feedback" ? (
              <>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Description</label>
                <textarea value={editPubDescription} onChange={e => setEditPubDescription(e.target.value)} style={{ width: "100%", minHeight: 90, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box", resize: "none" as const }} />

                <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Photos ou vidéos souvenir</label>
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, marginBottom: 4 }}>
                  {editPubMedia.map(m => (
                    <div key={m.url} style={{ position: "relative", width: 76, height: 76, borderRadius: 10, overflow: "hidden", backgroundColor: "#000" }}>
                      {m.media_type === "video" ? (
                        <video src={m.url} style={{ width: "100%", height: "100%", objectFit: "cover" }} muted />
                      ) : (
                        <img src={m.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      )}
                      <button type="button" onClick={() => removeEditFeedbackMedia(m.url)} style={{ position: "absolute", top: 3, right: 3, width: 20, height: 20, borderRadius: "50%", border: "none", backgroundColor: "rgba(0,0,0,0.65)", color: "white", fontSize: 12, lineHeight: "20px", cursor: "pointer", padding: 0 }}>✕</button>
                    </div>
                  ))}
                  {editPubMedia.length < MAX_EDIT_FEEDBACK_MEDIA && (
                    <label style={{ width: 76, height: 76, borderRadius: 10, border: "2px dashed #E0E0E0", backgroundColor: "#F8F8F8", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexDirection: "column" as const, gap: 2 }}>
                      <input type="file" accept="image/*,video/*" multiple onChange={handleEditFeedbackMediaSelect} style={{ display: "none" }} />
                      <span style={{ fontSize: 20, color: "#999" }}>+</span>
                      <span style={{ fontSize: 9, color: "#999" }}>{uploadingEditMedia ? "Envoi…" : "Ajouter"}</span>
                    </label>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "#999", marginBottom: 16 }}>{editPubMedia.length}/{MAX_EDIT_FEEDBACK_MEDIA} · comme sur Facebook ou LinkedIn, plusieurs photos ou vidéos possibles</div>

                <button onClick={saveEditPub} disabled={savingPub || !editPubDescription.trim() || editPubMedia.length === 0} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: savingPub ? 0.7 : 1 }}>
                  {savingPub ? "Enregistrement..." : "Enregistrer"}
                </button>
              </>
            ) : (
              <>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Titre</label>
                <input value={editPubTitle} onChange={e => setEditPubTitle(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box" }} />

                <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Description</label>
                <textarea value={editPubDescription} onChange={e => setEditPubDescription(e.target.value)} style={{ width: "100%", minHeight: 90, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box", resize: "none" as const }} />

                <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Photo de couverture</label>
                <label style={{ display: "block", border: "2px dashed #E0E0E0", borderRadius: 12, padding: 16, textAlign: "center", cursor: "pointer", backgroundColor: "#F8F8F8", marginBottom: 12 }}>
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) uploadPubCover(f); e.target.value = ""; }} />
                  {editPubCoverUrl ? (
                    <img src={editPubCoverUrl} alt={t(lang, "cr_preview_alt")} style={{ maxHeight: 130, borderRadius: 8 }} />
                  ) : (
                    <span style={{ fontSize: 13, color: "#999" }}>{uploadingPubCover ? "Envoi en cours..." : "+ Ajouter une photo"}</span>
                  )}
                  {editPubCoverUrl && !uploadingPubCover && <div style={{ fontSize: 12, fontWeight: 600, color: "#0a0a0a", marginTop: 8 }}>Changer la photo</div>}
                </label>

                <div onClick={() => setEditPubIsFree(!editPubIsFree)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", cursor: "pointer", marginBottom: 12 }}>
                  <div style={{ width: 40, height: 22, borderRadius: 12, backgroundColor: editPubIsFree ? "#0a0a0a" : "#E0E0E0", position: "relative", flexShrink: 0 }}>
                    <div style={{ position: "absolute", top: 2, left: editPubIsFree ? 20 : 2, width: 18, height: 18, borderRadius: "50%", backgroundColor: "white" }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Gratuit</span>
                </div>

                {!editPubIsFree && (
                  <>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Prix par personne (€)</label>
                    <input type="number" min={0} value={editPubPrice} onChange={e => setEditPubPrice(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box" }} />
                  </>
                )}

                <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Nombre de places (vide = illimité)</label>
                <input type="number" min={1} value={editPubMaxPlaces} onChange={e => setEditPubMaxPlaces(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 16, boxSizing: "border-box" }} />

                <button onClick={saveEditPub} disabled={savingPub || !editPubTitle} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: savingPub ? 0.7 : 1 }}>
                  {savingPub ? "Enregistrement..." : "Enregistrer"}
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {deletingPub && ReactDOM.createPortal(
        <div onClick={() => setDeletingPub(null)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 17, fontWeight: 700 }}>Supprimer "{deletingPub.title}" ?</span>
              <button onClick={() => setDeletingPub(null)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            <p style={{ fontSize: 13, color: "#666", lineHeight: 1.6, marginBottom: 14 }}>
              Cette action est définitive. S'il y a des participants inscrits ou des demandes en attente, indique une raison, elle leur sera envoyée. La suppression est refusée à moins de 72h de l'événement s'il y a des participants.
            </p>
            <textarea value={deleteReason} onChange={e => setDeleteReason(e.target.value)} placeholder="Raison (si des participants sont inscrits)" style={{ width: "100%", minHeight: 70, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box", resize: "none" as const }} />
            {deleteError && <div style={{ fontSize: 13, color: "#c0392b", marginBottom: 12 }}>{deleteError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setDeletingPub(null)} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#0a0a0a", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Annuler</button>
              <button onClick={confirmDeletePub} disabled={deletingSubmitting} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", backgroundColor: "#c0392b", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: deletingSubmitting ? 0.7 : 1 }}>
                {deletingSubmitting ? "Suppression..." : "Supprimer"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {cropTarget && (
        <ImageCropModal
          file={cropTarget.file}
          aspect={cropTarget.type === "avatar" ? 1 : 16 / 9}
          shape={cropTarget.type === "avatar" ? "circle" : "rect"}
          title={cropTarget.type === "avatar" ? "Recadrer la photo de profil" : "Recadrer la couverture"}
          onCancel={() => setCropTarget(null)}
          onConfirm={confirmCrop}
        />
      )}
    </div>
  );
}
